import { useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import { ApiError } from '../api/client';
import { usePermissions } from '../auth/usePermissions';
import {
  getFiscalProfile,
  listInvoices,
  publicInvoiceUrl,
  saveFiscalProfile,
  voidInvoice,
} from '../api/endpoints/invoices';
import { createIdempotencyKeyHolder } from '../api/idempotency';
import type { InvoiceRecord } from '../api/schemas';
import { AutoInvoiceSwitch } from '../components/AutoInvoiceSwitch';
import { EmptyState } from '../components/EmptyState';
import { ErrorBanner } from '../components/ErrorBanner';
import { FormField } from '../components/FormField';
import { InvoiceMailFeedback, InvoiceMailForm, useInvoiceMail } from '../components/InvoiceMail';
import { LoadingState } from '../components/LoadingState';
import { PageHeader } from '../components/PageHeader';
import { formatDateTime, formatMoneyCents } from '../lib/format';

export const FISCAL_PROFILE_QUERY_KEY = ['business-fiscal-profile'];
export const INVOICES_QUERY_KEY = ['business-invoices'];

const DEFAULT_ITBMS_PERCENT = '7';

/** Basis points shown to the owner as a plain percent ('700' -> '7'). */
function bpToPercentInput(bp: number): string {
  return String(bp / 100);
}

function percentInputToBp(value: string): number | undefined {
  const percent = Number(value);
  if (!Number.isFinite(percent) || percent < 0 || percent > 20) return undefined;
  return Math.round(percent * 100);
}

export function FacturasPage() {
  // Issuing invoices is `facturas`; the fiscal data they carry belongs to whoever edits the business.
  const editsFiscalData = usePermissions().can('negocio');
  const profileQuery = useQuery({
    queryKey: FISCAL_PROFILE_QUERY_KEY,
    queryFn: getFiscalProfile,
    retry: false,
    enabled: editsFiscalData,
  });
  const invoicesQuery = useQuery({ queryKey: INVOICES_QUERY_KEY, queryFn: listInvoices });
  const invoices = invoicesQuery.data?.data ?? [];
  const profileMissing =
    profileQuery.isError &&
    profileQuery.error instanceof ApiError &&
    profileQuery.error.status === 404;

  return (
    <main>
      <PageHeader
        title="Facturas"
        description="Tus facturas numeradas y los datos fiscales que llevan impresos."
      />

      <section className="section" aria-label="Facturas emitidas">
        <h2 className="section-label">Emitidas</h2>
        {invoicesQuery.isLoading ? <LoadingState label="Cargando facturas…" /> : null}
        {invoicesQuery.isError ? (
          <ErrorBanner message={errorMessage(invoicesQuery.error)} />
        ) : null}
        {invoicesQuery.data ? (
          invoices.length === 0 ? (
            <EmptyState
              title="Todavía no has emitido facturas."
              description="Cobra en el mostrador y emite la factura desde el mismo cobro."
            />
          ) : (
            <ul className="rows">
              {invoices.map((invoice) => (
                <li key={invoice.id}>
                  <InvoiceRow invoice={invoice} />
                </li>
              ))}
            </ul>
          )
        ) : null}
      </section>

      {editsFiscalData ? (
        <section className="section" aria-label="Datos fiscales">
          <h2 className="section-label">Datos fiscales</h2>
          <AutoInvoiceSwitch />
          {profileQuery.isLoading ? <LoadingState label="Cargando datos fiscales…" /> : null}
          {profileQuery.isError && !profileMissing ? (
            <ErrorBanner message={errorMessage(profileQuery.error)} />
          ) : null}
          {profileMissing ? (
            <p className="notice">
              Configura estos datos una vez y cada factura saldrá con ellos impresos.
            </p>
          ) : null}
          {!profileQuery.isLoading ? (
            <FiscalProfileForm
              initial={
                profileQuery.data
                  ? {
                      ruc: profileQuery.data.ruc,
                      dv: profileQuery.data.dv,
                      razonSocial: profileQuery.data.razonSocial,
                      direccion: profileQuery.data.direccion,
                      itbms: bpToPercentInput(profileQuery.data.itbmsDefaultRateBp),
                    }
                  : undefined
              }
            />
          ) : null}
        </section>
      ) : null}
    </main>
  );
}

type FiscalProfileFormProps = {
  initial?: { ruc: string; dv: string; razonSocial: string; direccion: string; itbms: string };
};

function FiscalProfileForm({ initial }: FiscalProfileFormProps) {
  const queryClient = useQueryClient();
  const [razonSocial, setRazonSocial] = useState(initial?.razonSocial ?? '');
  const [ruc, setRuc] = useState(initial?.ruc ?? '');
  const [dv, setDv] = useState(initial?.dv ?? '');
  const [direccion, setDireccion] = useState(initial?.direccion ?? '');
  const [itbmsInput, setItbmsInput] = useState(initial?.itbms ?? DEFAULT_ITBMS_PERCENT);
  const [itbmsError, setItbmsError] = useState<string | undefined>();
  const [saved, setSaved] = useState(false);

  const saveMutation = useMutation({
    mutationFn: (itbmsDefaultRateBp: number) =>
      saveFiscalProfile({ ruc, dv, razonSocial, direccion, itbmsDefaultRateBp }),
    onSuccess: () => {
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: FISCAL_PROFILE_QUERY_KEY });
    },
  });

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setSaved(false);
    const bp = percentInputToBp(itbmsInput);
    if (bp === undefined) {
      setItbmsError('Escribe un porcentaje entre 0 y 20, por ejemplo 7.');
      return;
    }
    setItbmsError(undefined);
    saveMutation.mutate(bp);
  }

  return (
    <form className="card form-grid cols-2" onSubmit={handleSubmit}>
      <FormField label="Razón social" htmlFor="fiscal-razon" className="span-2">
        <input
          id="fiscal-razon"
          value={razonSocial}
          onChange={(event) => setRazonSocial(event.target.value)}
          required
        />
      </FormField>
      <FormField label="RUC" htmlFor="fiscal-ruc">
        <input id="fiscal-ruc" value={ruc} onChange={(event) => setRuc(event.target.value)} required />
      </FormField>
      <FormField label="DV" htmlFor="fiscal-dv">
        <input id="fiscal-dv" value={dv} onChange={(event) => setDv(event.target.value)} required />
      </FormField>
      <FormField label="Dirección" htmlFor="fiscal-direccion" className="span-2">
        <input
          id="fiscal-direccion"
          value={direccion}
          onChange={(event) => setDireccion(event.target.value)}
          required
        />
      </FormField>
      <FormField
        label="ITBMS por defecto (%)"
        htmlFor="fiscal-itbms"
        error={itbmsError}
        hint="El 7 de siempre, salvo que tu actividad use otra tasa."
      >
        <input
          id="fiscal-itbms"
          inputMode="decimal"
          value={itbmsInput}
          onChange={(event) => setItbmsInput(event.target.value)}
          required
        />
      </FormField>
      {saveMutation.isError ? <ErrorBanner message={errorMessage(saveMutation.error)} /> : null}
      {saved ? <p className="field-help span-2">Datos fiscales guardados.</p> : null}
      <button type="submit" className="btn" disabled={saveMutation.isPending}>
        {saveMutation.isPending ? 'Guardando…' : 'Guardar datos fiscales'}
      </button>
    </form>
  );
}

type InvoiceRowProps = { invoice: InvoiceRecord };

function InvoiceRow({ invoice }: InvoiceRowProps) {
  const queryClient = useQueryClient();
  const voids = usePermissions().can('anular');
  const [voiding, setVoiding] = useState(false);
  const [reason, setReason] = useState('');
  const voidKey = useRef(createIdempotencyKeyHolder()).current;
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState<'idle' | 'done' | 'failed'>('idle');
  const mail = useInvoiceMail(invoice.id, () => setSending(false));
  const shareUrl = publicInvoiceUrl(window.location.origin, invoice.shareToken);

  async function copyShareLink() {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied('done');
    } catch {
      // No clipboard permission, or an insecure context. Showing the link is
      // more use than an apology: the owner copies it by hand.
      setCopied('failed');
    }
  }

  const voidMutation = useMutation({
    mutationFn: () => voidInvoice(invoice.id, reason.trim(), voidKey.ensure()),
    onSuccess: () => {
      setVoiding(false);
      setReason('');
      void queryClient.invalidateQueries({ queryKey: INVOICES_QUERY_KEY });
    },
    onSettled: () => voidKey.reset(),
  });

  const anulada = invoice.status === 'anulada';

  return (
    <div className="row">
      <div className="grow">
        <p className="row-title">
          {invoice.displayNumber}{' '}
          <span className="status" data-tone={anulada ? 'off' : 'good'}>
            {anulada ? 'Anulada' : 'Emitida'}
          </span>
        </p>
        <p className="row-sub">
          {formatDateTime(invoice.issuedAt)}
          {invoice.clientName ? ` · ${invoice.clientName}` : ''}
          {anulada && invoice.voidReason ? ` · ${invoice.voidReason}` : ''}
        </p>
        {voidMutation.isError ? <ErrorBanner message={errorMessage(voidMutation.error)} /> : null}
        <InvoiceMailFeedback mail={mail} />
        {copied === 'done' ? <p className="field-help">Enlace copiado.</p> : null}
        {copied === 'failed' ? (
          <p className="field-help">Copia este enlace: {shareUrl}</p>
        ) : null}
        {sending ? (
          <InvoiceMailForm invoiceId={invoice.id} mail={mail} onCancel={() => setSending(false)} />
        ) : null}
        {voiding ? (
          <form
            className="filter-row"
            onSubmit={(event: FormEvent) => {
              event.preventDefault();
              if (reason.trim() === '') return;
              voidMutation.mutate();
            }}
          >
            <div className="field">
              <label className="field-label" htmlFor={`void-reason-${invoice.id}`}>
                Motivo de anulación
              </label>
              <input
                id={`void-reason-${invoice.id}`}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="Cobro duplicado"
                required
              />
            </div>
            <button type="submit" className="btn small danger" disabled={voidMutation.isPending}>
              {voidMutation.isPending ? 'Anulando…' : 'Anular factura'}
            </button>
            <button
              type="button"
              className="btn small ghost"
              onClick={() => setVoiding(false)}
              disabled={voidMutation.isPending}
            >
              Cancelar
            </button>
          </form>
        ) : null}
      </div>
      <span className="amount">{formatMoneyCents(invoice.totalCents)}</span>
      <span className="action-pair">
        <Link
          to="/facturas/$invoiceId/imprimir"
          params={{ invoiceId: invoice.id }}
          className="btn small secondary"
        >
          Imprimir
        </Link>
        {!sending ? (
          <button
            type="button"
            className="btn small secondary"
            onClick={() => {
              setCopied('idle');
              setSending(true);
            }}
          >
            Enviar por correo
          </button>
        ) : null}
        <button type="button" className="btn small ghost" onClick={() => void copyShareLink()}>
          Copiar enlace
        </button>
        {voids && !anulada && !voiding ? (
          <button type="button" className="btn small danger" onClick={() => setVoiding(true)}>
            Anular
          </button>
        ) : null}
      </span>
    </div>
  );
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'No pudimos completar eso ahora mismo.';
}
